import { useRequestURL } from 'nuxt/app'

import { NOINDEX_NOFOLLOW, type SeoImage, type SeoSiteProfile } from '@common/core-layer/composables/seo/useSeo'
import { getConfig } from '@common/core-layer/config'
import { isSafeSeoHttpUrl } from '@common/core-layer/utils/seo.utils'
import type { useLocationStore } from '@common/location-layer/store/location.store'

/**
 * Client Portal SEO site profile.
 *
 * Client Portal is white-labelled per location, so every brand value comes from portal settings
 * rather than a build-time constant. The `init-location` plugin resolves those settings during SSR
 * before page setup, so this reads a populated store on the server.
 *
 * Call `useLocationStore()` in component setup (while Pinia is active) and pass that instance in.
 * `provideSeoSiteProfile` wraps the factory in a computed that Unhead may re-evaluate after the
 * current Vue instance / active Pinia is gone — calling `useLocationStore()` inside the factory
 * then 500s. Reading a captured store is safe; branding still updates because the store is reactive.
 *
 * Defaults here are deliberately conservative: `noindex,nofollow`, so any page that does not opt in
 * with an explicit policy stays out of the index.
 */

const CLIENT_PORTAL_FALLBACK_SITE_NAME = 'Client Portal'

type LocationStore = ReturnType<typeof useLocationStore>

function resolveFallbackOgImage(alt: string): SeoImage {
  const bucket = getConfig().CLIENT_PORTAL_STORAGE_BUCKET?.trim()
  const bucketName = bucket || 'revex-client-portal-production'
  return {
    url: `https://storage.googleapis.com/${bucketName}/assets/force-app-update-bg.png`,
    alt,
  }
}

/** Portal banner/logo art, dropped unless it is an http(s) URL a crawler can fetch. */
function resolveClientPortalOgImage(store: LocationStore): SeoImage {
  const { branding } = store
  const candidates = [branding?.bannerImage, branding?.portalBannerImage, branding?.portalImage, branding?.logo]

  const alt = branding?.portalBannerText?.trim() || CLIENT_PORTAL_FALLBACK_SITE_NAME

  for (const candidate of candidates) {
    const url = candidate?.trim()
    if (url && isSafeSeoHttpUrl(url)) {
      return { url, alt }
    }
  }

  return resolveFallbackOgImage(alt)
}

/**
 * Canonical origin for this portal: the tenant's own custom domain when configured, else the
 * origin the request actually arrived on. Never a platform-wide constant — two locations must not
 * canonicalize to the same host.
 */
function resolveClientPortalBaseUrl(store: LocationStore): string {
  const customDomain = store.customDomain?.trim()
  if (customDomain) {
    const host = customDomain.replace(/^https?:\/\//i, '').replace(/\/+$/, '')
    if (host) return `https://${host}`
  }

  try {
    const origin = useRequestURL()?.origin
    if (origin && origin !== 'null') return origin.replace(/\/+$/, '')
  } catch {
    // Outside a Nuxt request context (client navigation, tests).
  }

  const browserOrigin = globalThis.location?.origin
  if (browserOrigin && browserOrigin !== 'null') return browserOrigin.replace(/\/+$/, '')

  return ''
}

function resolveClientPortalSiteName(store: LocationStore): string {
  const name = store.branding?.portalBannerText?.trim() || store.locationData?.portalBannerText?.trim()
  return name || CLIENT_PORTAL_FALLBACK_SITE_NAME
}

function resolveClientPortalDescription(store: LocationStore): string {
  const description = store.branding?.portalDescription?.trim() || store.locationData?.portalDescription?.trim()
  return description || resolveClientPortalSiteName(store)
}

export function createClientPortalSeoSiteProfile(store: LocationStore): SeoSiteProfile {
  const siteName = resolveClientPortalSiteName(store)

  return {
    siteName,
    baseUrl: resolveClientPortalBaseUrl(store),
    defaultDescription: resolveClientPortalDescription(store),
    defaultImage: resolveClientPortalOgImage(store),
    // Opt-in indexing: only pages with an explicit policy (e.g. community about) may index.
    defaultRobots: NOINDEX_NOFOLLOW,
    defaultOgType: 'website',
    defaultTwitterCard: 'summary_large_image',
    /**
     * `{Page} | {Portal}` — the portal's own name, never "GoKollab". A community opened in a
     * Client Portal belongs to that portal's brand.
     *
     * `useBrandedHead` applies the same shape via `titleTemplate` for pages that do not call
     * `useSeo`. Pages that do call it clear that template (see `useSeo`), so the suffix is applied
     * exactly once and `og:title` matches `<title>`.
     */
    formatTitle: pageTitle => {
      const trimmed = pageTitle?.trim()
      return trimmed ? `${trimmed} | ${siteName}` : siteName
    },
  }
}
